import { Trans, t } from '@lingui/macro';
import hotkeys from 'hotkeys-js';
import { ChevronUp, Search, Star, X, Users, ArrowLeftRight, Check, SlidersHorizontal, Bookmark, LayoutGrid, Plus } from 'lucide-react';
import { RefObject, useEffect, useReducer, useState, useMemo, useRef } from 'react';
import { useHistory, useLocation } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { useMyPermissions } from '../../context/WorkspacePermissionContext';
import { Room } from '../../models/Room';
import { User } from '../../models/User';
import { Workspace, WorkspaceMember } from '../../models/Workspace';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import { Util } from '../../services/Utils/Util';
import CreateRoomModal from '../components/CreateRoomModal';
import { ProfilePart } from '../components/ProfilePart';
import WorkspaceCreateModal from '../workspaces/WorkspaceCreateModal';
import WorkspaceJoinModal from '../workspaces/WorkspaceJoinModal';
import RoomItem from './LeftMenuRoomItem';
import RoomManagerModal from './RoomManagerPopover';

import { useMessageContext } from '../main/NotificationModal';
import UserSelectModal from '../components/UserSelectModal';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { cn } from '../../lib/utils';
import { IconChevronDown, IconMoreHorizontal, IconPlus } from '../components/FigmaIcons';
var PATH_FAVORITES = '/favorites';
var PATH_EXT_SHARES = '/shares';

const LeftMenuRooms: React.FC = () => {
  const { workspaces, rooms, setRooms, setIsMobileMenuOpen, activeCalls, roomsLoaded, setRoomsLoaded, activeWorkspaceId, setActiveWorkspaceId } = useAppUIContext();
  const [showMenu, setShowMenu] = useState<{ myRooms: boolean, one2one: boolean, showHiddenWSGroup: string[] }>({ myRooms: true, one2one: true, showHiddenWSGroup: [] });
  const [currentPath, setCurrentPath] = useState<string>();
  const [pathEnd, setPathEnd] = useState<string>('');
  const [filteredRooms, setFilterdRooms] = useState<Room[]>([]);
  // channels context를 초기값으로 사용 (모바일에서 Sheet 열릴 때 이미 데이터가 있을 수 있음)
  const [allRooms, setAllRooms] = useState<Room[]>(() => rooms || []);
  const [filterKey, setFilterKey] = useState<string | null>();
  const [workspaceRooms, setWorkspaceRooms] = useState(new Map<Workspace, Room[]>());
  const [one2oneRooms, setOne2OneRooms] = useState<Room[]>([]);
  const [personalRooms, setPersonalRooms] = useState<Room[]>([]);
  const [notifsByRoom] = useState(new Map<string, number>()); // roomId, nrNotifs (노출 대상만)
  const history = useHistory();
  const location = useLocation();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const { notification } = useMessageContext();
  const [isFavorite, setIsFavorite] = useState(LocalStorage.get('last-is-favorite') === 'favorite' ? true : false);
  const [showUserSelectModal, setShowUserSelectModal] = useState(false);
  const [showCreateWSModal, setShowCreateWSModal] = useState(false);
  const [showJoinWSModal, setShowJoinWSModal] = useState(false);
  const [showRoomManager, setShowRoomManager] = useState(false);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [wsSwitcherOpen, setWsSwitcherOpen] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState<User[]>([]);

  const perms = useMyPermissions();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    hotkeys('f', function (event, handler) {
      event.preventDefault();
      inputRef.current?.focus();
    });

    hotkeys('cmd+k, ctrl+k', function (event, handler) {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent('open-workspace-sitemap', { detail: { focusInput: true } }));
    });

    // 방 목록은 useInitialNavigation에서 이미 구독하고 있음
    // 여기서는 AppUIContext의 rooms를 사용하여 UI 업데이트만 처리



    // 알림 업데이트는 App.tsx에서 Firestore 구독으로 관리됨
    // 여기서는 MyNotifs 변경 이벤트만 구독하여 UI 업데이트
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates((notifs) => {
      notifsByRoom.clear();
      MyNotifs.NotificationsByRoom.forEach((notifs, roomId) => {
        // 음소거·외부 API 비활성화 등으로 노출 대상이 아닌 알림은 제외 (통지센터·벨과 동일 기준).
        // → 음소거 채널은 unread 0 이 되어 좌측 메뉴에 점·숫자 모두 안 뜬다.
        const filteredCount = notifs.filter(n => n.isNew && n.type != 'RoomAdded' && n.type != 'CallStart' && MyNotifs.isVisibleInCenter(n, My.RoomSettings)).length;
        notifsByRoom.set(roomId, filteredCount);
      });
      forceUpdate();
    });
    setCurrentPath(window.location.pathname.replace(/^\/(channel|room)\//, ''));
    setPathEnd([PATH_FAVORITES, PATH_EXT_SHARES].find(p => window.location.pathname?.endsWith(p)) || '');
    function handler(event: MessageEvent) {
      if (event.data?.type === 'go-to-room' && event.data.roomId) {
        history.replace('/channel/' + event.data.roomId);
      }
    }
    window.addEventListener('message', handler);

    My.updateRoomSettings();
    const unsubRoomSettings = My.subscribeToRoomSettingUpdates(() => { forceUpdate() });
    return () => {
      unsubNotifs();
      unsubRoomSettings();
      hotkeys.unbind('f');
      hotkeys.unbind('cmd+k, ctrl+k');
      window.removeEventListener('message', handler);
    }
  }, [])

  // Track location changes (react-router v6 pattern)
  useEffect(() => {
    setCurrentPath(location.pathname.replace(/^\/(channel|room)\//, ''));
    setPathEnd([PATH_FAVORITES, PATH_EXT_SHARES].find(p => location.pathname?.endsWith(p)) || '');
  }, [location.pathname]);

  // channels context가 변경될 때 UI 업데이트 (useInitialNavigation에서 rooms가 설정됨)
  // 모바일에서는 Sheet가 열릴 때 이 컴포넌트가 마운트되므로,
  // 마운트 시점에 이미 rooms가 설정되어 있는 경우도 처리해야 함
  useEffect(() => {
    if (rooms && rooms.length >= 0) {
      My.setRooms(rooms);
      setAllRooms(rooms);
      // changeRoomLists는 isFavorite, filterKey를 클로저로 캡처하므로
      // 직접 로직을 여기서 실행
      let roomList = [...rooms];
      if (filterKey) {
        roomList = roomList.filter(r => r.visibility === 'personal' || r.name.toLocaleLowerCase().includes(filterKey.toLocaleLowerCase()));
      }
      if (isFavorite) {
        roomList = roomList.filter(r => My.RoomSettings.get(r.id)?.favoriteRoomInMyList);
      }

      const _one2oneRooms: Room[] = [];
      const _personalRooms: Room[] = [];
      const _otherRooms: Room[] = [];

      roomList.forEach(r => {
        if (r.visibility === 'personal') {
          // 현재 워크스페이스에 속하거나 워크스페이스 없는 마이룸만 표시
          if (!r.workspaceId || r.workspaceId === activeWorkspaceId) {
            _personalRooms.push(r);
          }
        } else if (r.isOneToOne) {
          // 현재 워크스페이스에 속하거나 워크스페이스 없는 1:1만 표시
          if (r.workspaceId === activeWorkspaceId || !r.workspaceId) {
            _one2oneRooms.push(r);
          }
        } else {
          _otherRooms.push(r);
        }
      });

      // roomOrder/oneToOneOrder 순서 적용
      const applyOrder = (list: Room[], order: string[], fallbackSort: (a: Room, b: Room) => number) => {
        if (!order || order.length === 0) {
          return [...list].sort(fallbackSort);
        }
        const ordered = list.filter(r => order.includes(r.id)).sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
        const unordered = list.filter(r => !order.includes(r.id)).sort(fallbackSort);
        return [...ordered, ...unordered];
      };

      setFilterdRooms(applyOrder(_otherRooms, My.user.roomOrder ?? [], (a, b) => a.name.localeCompare(b.name)));
      setOne2OneRooms(applyOrder(_one2oneRooms, My.user.oneToOneOrder ?? [], (a, b) => {
        const aDate = a.created instanceof Date ? a.created.getTime() : 0;
        const bDate = b.created instanceof Date ? b.created.getTime() : 0;
        return bDate - aDate;
      }));
      setPersonalRooms(_personalRooms);

      // 만약 선택된 activeWorkspaceId가 없고, 가입된 워크스페이스가 있다면 첫 번째 워크스페이스를 기본으로 선택
      if (!activeWorkspaceId && workspaces.length > 0) {
        setActiveWorkspaceId(workspaces[0].id);
      }
    }
  }, [rooms, workspaces, isFavorite, filterKey, _, activeWorkspaceId]);

  useEffect(() => {
    let map = new Map<Workspace, Room[]>();
    workspaces.forEach(w => map.set(w, []));
    const guestWS = new Workspace("", t`Guest`, My.user);

    filteredRooms.forEach(r => {
      if (r.workspaceId) {
        const ws = workspaces.find(w => w.id == r.workspaceId) ?? guestWS;
        if (!map.has(ws)) {
          map.set(ws, []);
        }
        map.get(ws)!.push(r);
      }
    })

    const privateRooms = filteredRooms.filter(r => !r.workspaceId);
    if (privateRooms.length > 0) {
      map.set(new Workspace("", t`Private`, My.user), privateRooms);
    }
    setWorkspaceRooms(map);
  }, [workspaces, filteredRooms])
  useEffect(() => {
    if (notification) {
      // menuRef?.current?.close();
    }
  }, [notification]);


  useEffect(() => {
    changeRoomLists(allRooms, filterKey);
    LocalStorage.set('last-is-favorite', isFavorite ? "favorite" : "");
  }, [isFavorite]);



  // 워크스페이스 멤버 로드
  const prevWsIdRef = useRef<string | null | undefined>();
  useEffect(() => {
    // 워크스페이스가 전환된 직후에는 perms가 이전 워크스페이스 것일 수 있으므로 스킵
    if (prevWsIdRef.current !== activeWorkspaceId) {
      prevWsIdRef.current = activeWorkspaceId;
      setWorkspaceMembers([]);
      return;
    }

    if (!activeWorkspaceId || !perms || perms.canSearchMembers !== true) {
      setWorkspaceMembers([]);
      return;
    }
    Fire.ws.getWorkspaceMembers(activeWorkspaceId).then(members => {

      // 이미 1:1 대화방이 있는 유저 제외 (워크스페이스 무관, 전체 방에서 체크)
      const existingUserIds = new Set<string>();
      allRooms.filter(r => r.isOneToOne).forEach(room => {
        room.users.forEach(user => {
          if (user.id !== My.user.id) {
            existingUserIds.add(user.id);
          }
        });
      });

      const users = members
        .filter(m => m.user.id !== My.user.id && !existingUserIds.has(m.user.id))
        .map(m => new User(m.user.id, m.user.name, m.user.email, m.user.photoUrl ?? null, ''));
      setWorkspaceMembers(users);
    }).catch(err => {
      console.error('Failed to load workspace members:', err);
      setWorkspaceMembers([]);
    });
  }, [activeWorkspaceId, workspaces, allRooms, perms]);

  // useMemoGetRoomList를 useState/useEffect 선언 바로 아래로 이동


  function changeRoomLists(roomList: Room[], key?: string | null) {
    let _one2oneRooms: Room[] = [];
    let _personalRooms: Room[] = [];
    let _otherRooms: Room[] = [];
    if (key) {
      roomList = roomList.filter(r => r.visibility === 'personal' || r.name.toLocaleLowerCase().includes(key.toLocaleLowerCase()) || r.users?.some(u => u.name?.toLocaleLowerCase().includes(key.toLocaleLowerCase()) || u.email?.toLocaleLowerCase().includes(key.toLocaleLowerCase())));
    }
    if (isFavorite) {
      roomList = roomList.filter(r => My.RoomSettings.get(r.id)?.favoriteRoomInMyList);
    }
    roomList.forEach(r => {
      if (r.visibility === 'personal') {
        // 현재 워크스페이스에 속하거나 워크스페이스 없는 마이룸만 표시
        if (!r.workspaceId || r.workspaceId === activeWorkspaceId) {
          _personalRooms.push(r);
        }
      } else if (r.isOneToOne) {
        // 현재 워크스페이스에 속하거나 워크스페이스 없는 1:1만 표시
        if (r.workspaceId === activeWorkspaceId || !r.workspaceId) {
          _one2oneRooms.push(r);
        }
      } else
        _otherRooms.push(r);
    })

    setFilterdRooms(Room.sortRoomsByName(_otherRooms));
    setOne2OneRooms(Room.sortRoomsByDate(_one2oneRooms));
    setPersonalRooms(_personalRooms);
    setFilterKey(key);
  }
  // Legacy (Workspaceless) rooms
  const useMemoGetRoomList = useMemo(() => {
    let activeWS = workspaces.find(w => w.id === activeWorkspaceId);
    if (!activeWS && workspaces.length > 0) activeWS = workspaces[0];

    const currentWSRooms = activeWS ? (workspaceRooms.get(activeWS) || []) : [];

    return (
      <div>
        {/* Active Workspace Channels */}
        {activeWS && (
          <div>
            {currentWSRooms.length == 0 && <div className='px-8 py-2 text-xs text-muted-foreground italic'><Trans>No joined channels</Trans></div>}
            <div className="space-y-0.5 mt-0.5">
              {currentWSRooms.filter(r => !My.RoomSettings.get(r.id)?.hideRoomInMyList).map(room => roomItem(room))}
              {getHiddenRoomPart(activeWS, currentWSRooms.filter(r => My.RoomSettings.get(r.id)?.hideRoomInMyList))}
            </div>
          </div>
        )}
      </div>
    );
  }, [workspaceRooms, filterKey, isFavorite, currentPath, showMenu, notification, activeWorkspaceId, workspaces, activeCalls, _]);

  // 워크스페이스별 알림 카운트 계산
  const wsNotifCounts = useMemo(() => {
    const counts = new Map<string, number>();
    notifsByRoom.forEach((count, roomId) => {
      if (count <= 0) return;
      // mute(푸시 비활성화) 채널은 워크스페이스 카운트에서 제외 — LeftMenuHeader·통지벨과 일치
      if (My.RoomSettings.get(roomId)?.disablePushNotifications) return;
      const room = allRooms.find(r => r.id === roomId);
      if (room?.workspaceId) {
        counts.set(room.workspaceId, (counts.get(room.workspaceId) || 0) + count);
      }
    });
    return counts;
  }, [allRooms, _]); // _ (forceUpdate)로 notifsByRoom 변화 반영

  const otherWsNotifTotal = useMemo(() => {
    let total = 0;
    wsNotifCounts.forEach((count, wsId) => {
      if (wsId !== activeWorkspaceId) total += count;
    });
    return total;
  }, [wsNotifCounts, activeWorkspaceId]);



  return (
    <>
      <div className='flex flex-col w-full h-full overflow-hidden'>
        {/* legacy 개인 채널 + AI 메모는 LeftMenuHeader(워크스페이스 위)로 이동됨 */}
        {/* Figma: 사이드바 전체 px-4 → 스크롤 영역도 동일 패딩 */}
        <div className="flex-1 overflow-y-auto custom-scrollbar pb-4 pt-0 px-4">
          {workspaces.length > 0 ? (
            <>

              <div className="mb-4 flex flex-col gap-1">
                <div className="flex items-center justify-between group">
                  {getMenuTitle(t`Channels`, showMenu.myRooms, () => setShowMenu(p => ({ ...p, myRooms: !p.myRooms })))}
                  <div className="flex items-center gap-1 mr-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="icon-ghost">
                          <IconMoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" sideOffset={4}>
                        {perms?.canCreateRoom !== false && (
                          <DropdownMenuItem onClick={() => setShowCreateRoomModal(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            <Trans>New Channel</Trans>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setShowRoomManager(true)}>
                          <SlidersHorizontal className="w-4 h-4 mr-2" />
                          <Trans>Channel Manager</Trans>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => { history.push('/search'); if (window.innerWidth < 1200) setIsMobileMenuOpen(false); }}>
                          <Search className="w-4 h-4 mr-2" />
                          <Trans>Search</Trans>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { history.push('/channel/all-rooms/favorites'); if (window.innerWidth < 1200) setIsMobileMenuOpen(false); }}>
                          <Bookmark className="w-4 h-4 mr-2" />
                          <Trans>Saved Messages</Trans>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <RoomManagerModal
                      open={showRoomManager}
                      onOpenChange={(v) => {
                        setShowRoomManager(v);
                        if (!v) forceUpdate(); // 순서 변경 반영
                      }}
                      rooms={allRooms.filter(r => !r.isOneToOne && r.visibility !== 'personal' && (r.workspaceId === activeWorkspaceId || (!r.workspaceId && !activeWorkspaceId)))}
                      oneToOneRooms={one2oneRooms.filter(r => r.workspaceId === activeWorkspaceId || (!r.workspaceId && !activeWorkspaceId))}
                      onNavigateToRoom={(roomId) => {
                        history.push(`/channel/${roomId}`);
                        if (window.innerWidth < 1200) setIsMobileMenuOpen(false);
                      }}
                    />
                  </div>
                </div>

                {/* 방 검색 + 즐겨찾기 필터 (방 10개 초과 시 표시) */}
                {showMenu.myRooms && (allRooms.filter(r => r.workspaceId === activeWorkspaceId || (!r.workspaceId && !activeWorkspaceId)).length > 10 || !!filterKey || isFavorite) && (
                  <div className="flex items-center gap-1 py-1">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        className="w-full h-7 pl-7 pr-7 text-xs rounded-md border border-border bg-muted focus:bg-background focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground"
                        placeholder={t`Search channels...`}
                        value={filterKey ?? ''}
                        onChange={e => setFilterKey(e.target.value || null)}
                      />
                      {filterKey && (
                        <button className="absolute right-1.5 top-1/2 -translate-y-1/2" onClick={() => setFilterKey(null)}>
                          <X className="w-3.5 h-3.5 text-muted-foreground hover:text-[color:rgb(var(--text-body))]" />
                        </button>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-7 h-7 shrink-0"
                      onClick={() => setIsFavorite(!isFavorite)}
                    >
                      <Star className={cn("w-4 h-4 transition-colors", isFavorite ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground")} />
                    </Button>
                  </div>
                )}

                <div className='flex flex-col gap-0.5'>
                  {showMenu.myRooms && <>
                    {personalRooms.filter(r => r.workspaceId && r.workspaceId !== 'null').map(room => <div key={room.id}>{roomItem(room, false)}</div>)}
                    {useMemoGetRoomList}
                  </>}
                </div>
              </div>

              {one2oneRooms.length > 0 && <>
                <div className="border-b border-border my-3" />
                <div className="mb-4">
                  <div className="flex items-center justify-between group">
                    {getMenuTitle(t`Direct Messages`, showMenu.one2one, () => setShowMenu(p => ({ ...p, one2one: !p.one2one })), perms?.canSearchMembers !== false ? () => setShowUserSelectModal(true) : undefined)}
                  </div>
                  <div className='flex flex-col gap-0.5 mt-1'>
                    {showMenu.one2one && getOne2OneList()}
                  </div>
                </div>
              </>}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-[color:rgb(var(--text-body))] mb-1"><Trans>No workspace yet</Trans></p>
              <p className="text-xs text-muted-foreground"><Trans>Create or join a workspace to get started</Trans></p>
            </div>
          )}
        </div>
      </div>
      <UserSelectModal
        show={showUserSelectModal}
        title={t`1:1 Invite`}
        onClose={() => setShowUserSelectModal(false)}
        onSelect={createOneToOneRoom}
        users={workspaceMembers}
      />
      {showCreateWSModal && <WorkspaceCreateModal show={showCreateWSModal} setShow={setShowCreateWSModal} />}
      {showJoinWSModal && <WorkspaceJoinModal show={showJoinWSModal} setShow={setShowJoinWSModal} />}
      {showCreateRoomModal && <CreateRoomModal show={showCreateRoomModal} setShow={setShowCreateRoomModal} workspaceId={activeWorkspaceId || null} />}
    </>
  );

  function getOne2OneList() {
    return <>
      {one2oneRooms.filter(r => !My.RoomSettings.get(r.id)?.hideRoomInMyList).map(room => roomItem(room))}
      {getHiddenRoomPart(null, one2oneRooms.filter(r => My.RoomSettings.get(r.id)?.hideRoomInMyList))}
    </>
  }

  function filterRooms(key?: string | null) {
    changeRoomLists(allRooms, key);
  }

  function roomItem(room: Room, isLegacy = false) {
    let rc = activeCalls.get(room.id);
    let nrOfNotifs = notifsByRoom.get(room.id) ?? 0;
    let isSelected = currentPath?.startsWith(room.id) ?? false;
    let theOtherUser = Room.getOtherOne2OneUser(room, My.user.id);
    let disabledPush = My.RoomSettings.get(room.id)?.disablePushNotifications ?? false;
    const meInTheCall = !!rc?.userIds.find(id => id === My.user.id);


    return <div key={room.id}>
      <RoomItem room={room} roomCall={rc} nrOfNotifs={nrOfNotifs} isSelected={isSelected} theOtherUser={theOtherUser} meInTheCall={meInTheCall} pathEnd={pathEnd} disabledPush={disabledPush} isLegacy={isLegacy} />
    </div>
  }

  function getHiddenRoomPart(ws: Workspace | null, rooms: Room[], legacyId?: string) {
    const listId = legacyId || (ws ? ws.id + ws.name : 'one2one');

    if (rooms.length == 0)
      return <div key={`hidden-${listId}`}></div>;

    let nrOfNotifs = 0;
    rooms.forEach(r => nrOfNotifs += notifsByRoom.get(r.id) ?? 0);
    const showGroup = !!filterKey || showMenu.showHiddenWSGroup.includes(listId);
    const onClick = () => {
      setShowMenu(p => {
        const isCurrentlyShown = p.showHiddenWSGroup.includes(listId);
        if (isCurrentlyShown) {
          return { ...p, showHiddenWSGroup: p.showHiddenWSGroup.filter(name => name !== listId) };
        } else {
          return { ...p, showHiddenWSGroup: [...p.showHiddenWSGroup, listId] };
        }
      });
    }

    return <div className="pl-2 mt-1" key={`hidden-${listId}`}>
      {!isFavorite &&
        <div
          onClick={onClick}
          className="flex items-center gap-2 px-3 py-1.5 mx-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md cursor-pointer select-none transition-colors"
        >
          {showGroup ? <ChevronUp className="h-3 w-3" /> : <IconChevronDown className="h-3 w-3" />}
          <span><Trans>{rooms.length} hidden</Trans></span>
          {!!nrOfNotifs && (
            <div className="ml-auto flex items-center gap-1 pointer-events-none">
              <Badge variant="notification" className="">{nrOfNotifs}</Badge>
            </div>
          )}
        </div>
      }
      {showGroup && <div className="pl-3 border-l-2 border-border ml-5 my-1 space-y-0.5">
        {rooms.map(room => roomItem(room))}
      </div>
      }
    </div>
  }

  function getMenuTitle(title: string, expanded: boolean, onClickHandler: () => void, onAdd?: (e: any) => void) {
    return (
      <div
        className="flex items-center flex-1 cursor-pointer select-none py-0.5"
        onClick={onClickHandler}
        data-cy="room"
      >
        {/* Figma: chevron 16px + title text-label Bold Gray/400 */}
        <div className="flex items-center gap-1 text-label font-bold text-muted-foreground hover:text-foreground transition-colors uppercase">
          {expanded ? <IconChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          {title}
        </div>
        {onAdd && (
          <Button
            variant="icon-ghost"
            className="ml-auto mr-1"
            onClick={(e) => { e.stopPropagation(); onAdd(e); }}
          >
            <IconPlus />
          </Button>
        )}
      </div>
    );
  }



  async function createOneToOneRoom(user: User) {
    setShowUserSelectModal(false);
    let existingRoom = Array.from(My.Rooms.values()).find(r => r.isOneToOne && r.userIds.includes(user.id) && r.userIds.includes(My.user.id));
    if (!existingRoom) {
      let activeWS = workspaces.find(w => w.id === activeWorkspaceId);
      if (!activeWS && workspaces.length > 0) activeWS = workspaces[0];

      existingRoom = new Room(`${user.name}${Util.OneToOneDelimiter}${My.user.name}`, My.user.id);
      existingRoom.isOneToOne = true;
      existingRoom.visibility = 'direct';
      existingRoom.workspaceId = activeWS?.id || null;
      existingRoom.users = [My.user, user];
      existingRoom.userIds = [My.user.id, user.id];
      await Fire.saveRoom(existingRoom);
    }

    setTimeout(() => {
      history.replace('/channel/' + existingRoom!.id);
    }, 100);
  }

  function changePathEnd(end: string) {
    if (end == 'search') {
      history.push(`/search`);
    } else {
      const roomId = currentPath?.split('/')[0];
      history.replace(`/channel/${roomId}${end}`);
    }
    // menuRef?.current?.close();
  }

  function segmentMenu() {
    if (history.location.pathname.startsWith('/channel/') || history.location.pathname.startsWith('/room/')) {
      return <div className='no-border'>
        <div className='left-tab-menu'>
        </div>
      </div >
    }
  }

};

export default LeftMenuRooms;
